import polyline from '@mapbox/polyline';

import { SEARCH_AREA, type OsmEndpoints } from '../config';
import type { GeoProvider, LatLng, PlaceSuggestion } from '../types';

// Насколько далеко OSRM может притянуть точку к дороге, прежде чем
// мы считаем маршрут недостоверным
export const MAX_SNAP_DISTANCE_M = 1000;

type NominatimAddress = {
  road?: string;
  house_number?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  county?: string;
  state?: string;
  country?: string;
};

type NominatimResponse = {
  address?: NominatimAddress;
  display_name?: string;
  error?: string;
};

type PhotonFeature = {
  properties: {
    osm_type?: string;
    osm_id?: number;
    name?: string;
    street?: string;
    housenumber?: string;
    city?: string;
    state?: string;
    countrycode?: string;
  };
  geometry: { coordinates: [number, number] };
};

type PhotonResponse = { features: PhotonFeature[] };

type OsrmResponse = {
  code: string;
  routes?: Array<{ legs: Array<{ distance: number }>; geometry: string }>;
  waypoints?: Array<{ distance: number }>;
};

const NO_ACCESS_ERROR = 'Проверьте доступность подъезда к геокоординатам';

const joinParts = (parts: Array<string | undefined>): string =>
  [...new Set(parts.filter((part): part is string => !!part))].join(', ');

const getJson = async <T>(url: string, errorMessage: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(errorMessage);
  }
  return (await response.json()) as T;
};

const searchBbox = (): string => {
  const { lat, lng, radiusKm } = SEARCH_AREA;
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));
  return [lng - lngDelta, lat - latDelta, lng + lngDelta, lat + latDelta].join(',');
};

export function createOsmProvider(endpoints: OsmEndpoints): GeoProvider {
  return {
    async reverseGeocode(lat: number, lng: number): Promise<string> {
      const params = new URLSearchParams({
        format: 'jsonv2',
        lat: String(lat),
        lon: String(lng),
        'accept-language': 'ru',
        addressdetails: '1',
      });
      const body = await getJson<NominatimResponse>(
        `${endpoints.nominatimUrl}/reverse?${params}`,
        'Geocoding server is not availiable'
      );
      if (body.error) {
        throw new Error(`Server operation status ${body.error}`);
      }
      const address = body.address || {};
      const settlement =
        address.city || address.town || address.village || address.hamlet || address.county;
      const result = joinParts([address.road, address.house_number, settlement, address.state]);
      return result || address.country || body.display_name || '';
    },

    async route(points: LatLng[]) {
      const coordinates = points.map(({ lat, lng }) => `${lng},${lat}`).join(';');
      const params = new URLSearchParams({
        overview: 'simplified',
        geometries: 'polyline',
      });
      const body = await getJson<OsrmResponse>(
        `${endpoints.osrmUrl}/route/v1/driving/${coordinates}?${params}`,
        'Routing server is not available'
      );
      if (body.code === 'NoRoute') {
        throw new Error(NO_ACCESS_ERROR);
      }
      if (body.code !== 'Ok' || !body.routes?.length) {
        throw new Error(`Server operation status ${body.code}`);
      }
      // OSRM молча притягивает точку к ближайшей дороге, большой сдвиг даёт неверный километраж
      if ((body.waypoints || []).some(({ distance }) => distance > MAX_SNAP_DISTANCE_M)) {
        throw new Error(NO_ACCESS_ERROR);
      }
      const [route] = body.routes;
      return {
        legDistances: route.legs.map((leg) => leg.distance),
        geoJSON: polyline.toGeoJSON(route.geometry),
      };
    },

    async searchPlaces(query: string): Promise<PlaceSuggestion[]> {
      const params = new URLSearchParams({
        q: query,
        limit: '10',
        // Photon не знает ru, default отдаёт местные названия
        lang: 'default',
        bbox: searchBbox(),
        lat: String(SEARCH_AREA.lat),
        lon: String(SEARCH_AREA.lng),
      });
      const body = await getJson<PhotonResponse>(
        `${endpoints.photonUrl}/api?${params}`,
        'Places server is not available'
      );
      const suggestions = body.features
        .filter(({ properties }) => properties.countrycode === SEARCH_AREA.countryCode)
        .map(({ properties, geometry }) => {
          const label = joinParts([
            properties.name,
            properties.street,
            properties.housenumber,
            properties.city,
            properties.state,
          ]);
          const [longitude, latitude] = geometry.coordinates;
          return {
            name: label,
            address: label,
            placeId: `${properties.osm_type ?? ''}${properties.osm_id ?? ''}`,
            radius: 1,
            latitude,
            longitude,
          };
        });
      // OSM делит улицу на несколько линий с одинаковой подписью; в списке подпись служит
      // значением опции, поэтому оставляем только первую
      return suggestions.filter(
        (place, index) => suggestions.findIndex(({ name }) => name === place.name) === index
      );
    },

    async resolvePlace(place: PlaceSuggestion): Promise<LatLng> {
      if (place.latitude == null || place.longitude == null) {
        throw new Error('Place has no coordinates');
      }
      return { lat: place.latitude, lng: place.longitude };
    },
  };
}
